import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  CheckboxGroup,
  HeadingText,
  LoadingSpinner,
  SearchInput,
  Sheet,
} from '@paypalcorp/pp-react'
import { MessageFormat } from '@paypalcorp/worldready'
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react'
import apiClient from '../../api'
import PropTypes from 'prop-types'
import fpti from '../../instrumentation'
import errorConstants from '../errorConstants'

const checkIfAlreadyPresent = (accountNumber, entries) => {
  return entries.some((e) => e.accountNumber === accountNumber)
}

const renderPermissionsList = (permissions, handleChange) => {
  const perms = permissions
  return perms.map((permission, index) => {
    return (
      <div className="perm_div" id={`p${index}`} key={index}>
        <Checkbox
          className="tpp_checkbox"
          id={`add_${permission.name}`}
          label={<Message id={`thirdPartyPerm.${permission.text_ref}`} />}
          onChange={(e) => handleChange(permission.name, e.target.checked)}
        />
      </div>
    )
  })
}

const renderFooter = (isAddOpen, onAddSubmit, addBtnState) => {
  if (isAddOpen) {
    return (
      <div className="tppAdd_Buttons">
        <Button
          className="tpp_btn"
          onClick={onAddSubmit}
          btnState={addBtnState}
          id="addPermBtn"
          data-pa-click="addPermBtn"
        >
          <Message id="thirdPartyPerm.addButton" />
        </Button>
      </div>
    )
  }
  return null
}

const GrantAddPerm = (props) => {
  const worldReady = useContext(WorldReadyContext)
  const [apiUsername, setApiUsername] = useState('')
  const [addBtnState, setAddBtnState] = useState('initial')
  const [checkedPerms, setCheckedPerms] = useState([])
  const [party, setParty] = useState({})
  const [grantError, setGrantError] = useState(null)
  const [isAddOpen, setAddOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const successMsg = new MessageFormat(worldReady, {
    id: `thirdPartyPerm.addSuccess`,
  }).format({ api_username: party.apiUsername })

  const baseClass =
    props.windowWidth <= props.mobileScreenSize
      ? 'tpp_dialogMobile'
      : 'tpp_dialog'

  const onGrantSubmit = () => {
    setLoading(true)
    setAddOpen(false)
    if (apiUsername.length === 0) {
      props.errorHandler(
        { message: errorConstants.INVALID_API_USERNAME },
        setGrantError,
        worldReady,
        null,
        setLoading,
      )
      setAddOpen(false)
      setLoading(false)
      return
    }
    apiClient
      .get(`/lookupEntry?api_username=${apiUsername}`)
      .then((response) => {
        if (checkIfAlreadyPresent(response.data.accountNumber, props.entries)) {
          setGrantError(
            new MessageFormat(worldReady, {
              id: `thirdPartyPerm.userAlreadyPresentError`,
            }).format(),
          )
          fpti.logFPTIError(
            new Error('PARTY_ALREADY_PRESENT'),
            'Party already added',
            'PARTY_ALREADY_PRESENT',
          )
          setLoading(false)
        } else {
          setLoading(false)
          setGrantError(null)
          setParty({
            apiUsername: response.data.apiUsername,
            Organization: response.data.Organization,
            accountNumber: response.data.accountNumber,
          })
          setAddOpen(true)
        }
        return null
      })
      .catch((e) => {
        setAddOpen(false)
        props.errorHandler(e, setGrantError, worldReady, null, setLoading)
      })
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!loading && event.key === 'Enter') {
        onGrantSubmit()
      }
    }
    window.addEventListener('keyup', handleKeyDown)
    return () => {
      window.removeEventListener('keyup', handleKeyDown)
    }
  })

  const onAddSubmit = () => {
    if (checkedPerms.length === 0) {
      setGrantError(
        new MessageFormat(worldReady, {
          id: `thirdPartyPerm.NO_PERM_SELECTED`,
        }).format(),
      )
      return
    }
    const entry = {
      apiUsername: party.apiUsername,
      Organization: party.Organization,
      grantedPermissions: checkedPerms,
      accountNumber: party.accountNumber,
    }

    setAddBtnState('processing')
    apiClient
      .post(`/addPerm`, { entry })
      .then((response) => {
        setGrantError(null)
        setAddBtnState('success')
        props.closeSuccessSheet(successMsg, 'grant', response.data)
        return null
      })
      .catch((e) => {
        props.errorHandler(e, setGrantError, worldReady, setAddBtnState)
      })
  }

  const handleChange = (name, checked) => {
    if (checked) {
      setCheckedPerms([...checkedPerms, name])
    } else {
      setCheckedPerms(checkedPerms.filter((e) => e !== name))
    }
  }

  return (
    <Sheet
      noPadding
      // center
      showLogo
      isOpen={props.isGrantOpen}
      className={baseClass}
      onCloseComplete={() => {
        setAddOpen(false)
        setGrantError(null)
        props.setGrantOpen({ type: 'grant', value: false })
      }}
      footerAlign="center"
      footerContents={renderFooter(isAddOpen, onAddSubmit, addBtnState)}
      id="addSheet"
    >
      <div>
        {grantError && (
          <div className="tppError_div">
            <Alert type="error" id="grantErrorAlert" className="tppError_alert">
              {grantError}
            </Alert>
          </div>
        )}
      </div>
      <div>
        <HeadingText className="tppView_Heading view_heading">
          <Message id="thirdPartyPerm.grantHeading" />
        </HeadingText>
      </div>
      <div className="tppGrant_form view_subHeadingUpper">
        <SearchInput
          type="text"
          label={new MessageFormat(worldReady, {
            id: `thirdPartyPerm.usernameTextinputLabel`,
          }).format()}
          className="tppGrant_textInput"
          id="apiUsernameInput"
          onClear={() => setApiUsername('')}
          value={apiUsername}
          onChange={(e) => setApiUsername(e.target.value)}
        />
        {loading && <LoadingSpinner />}
      </div>
      {isAddOpen && (
        <div>
          <div className="view_subHeadingUpper">
            <div className="view_subHeading">
              <p>
                <Message id="thirdPartyPerm.username" />:{' '}
                <strong id="addApiUsername">{party.apiUsername}</strong>
              </p>
            </div>
            <div className="view_subHeading">
              <p>
                <Message id="thirdPartyPerm.organization" />:{' '}
                <strong id="addOrganizationName">{party.Organization}</strong>
              </p>
            </div>
          </div>
          <div className="checkBoxGroup">
            <div>
              <Message id="thirdPartyPerm.availablePermissions" />
            </div>
            <CheckboxGroup>{renderPermissionsList(props.permissions, handleChange)}</CheckboxGroup>
          </div>
        </div>
      )}
    </Sheet>
  )
}

GrantAddPerm.propTypes = {
  entries: PropTypes.array,
  isGrantOpen: PropTypes.any,
  permissions: PropTypes.array,
  setGrantOpen: PropTypes.func,
  errorHandler: PropTypes.func,
  closeSuccessSheet: PropTypes.func,
  windowWidth: PropTypes.any,
  mobileScreenSize: PropTypes.any,
}

export default GrantAddPerm
